import type {
  AttentionObject,
  BootstrapResponse,
  EvidenceRef,
  Signal,
  SourceAccount,
  SourceHealth,
} from "@aion/xo-core";

const iso = (offsetHours = 0): string =>
  new Date(Date.now() + offsetHours * 3_600_000).toISOString();

const evidence = (
  id: string,
  provider: EvidenceRef["provider"],
  label: string,
  url: string,
  excerpt: string,
): EvidenceRef => ({
  id,
  sourceRecordId: `source-${id}`,
  provider,
  label,
  url,
  excerpt,
  occurredAt: iso(-2),
});

const attention = ({
  id,
  domainId,
  title,
  summary,
  dueIn,
  score,
  state = "ACTIONABLE",
  disposition = "TODAY",
  sources,
  suggestedNextAction,
}: {
  id: string;
  domainId: string;
  title: string;
  summary: string;
  dueIn?: number;
  score: number;
  state?: AttentionObject["state"];
  disposition?: AttentionObject["disposition"];
  sources: EvidenceRef[];
  suggestedNextAction: string;
}): AttentionObject => ({
  id,
  userId: "demo-owner",
  domainId,
  title,
  summary,
  state,
  disposition,
  dueAt: dueIn === undefined ? null : iso(dueIn),
  deadlineOrigin: dueIn === undefined ? "NONE" : "SUGGESTED",
  deadlineReason: dueIn === undefined ? null : "Suggested from urgency and impact.",
  eventOccurredAt: iso(-2),
  confidence: 0.91,
  score,
  facts: sources.map((source, index) => ({
    id: `${id}-fact-${index}`,
    label: index === 0 ? "Source fact" : "Corroborating fact",
    value: source.excerpt || source.label,
    observedAt: source.occurredAt,
    evidenceRefIds: [source.id],
  })),
  inferences: [
    {
      id: `${id}-inference`,
      claim: summary,
      basisEvidenceRefIds: sources.map((source) => source.id),
      confidence: 0.84,
    },
  ],
  judgement: {
    summary: suggestedNextAction,
    rationale: "The consequence is time-bound and the next move belongs to you.",
    confidence: 0.91,
    model: "gpt-5.1",
    promptVersion: "xo-v1.0.0",
    createdAt: iso(-1),
  },
  evidence: sources,
  sourceAccountIds: Array.from(
    new Set(
      sources.map((source) =>
        source.provider === "gmail"
          ? "google-personal"
          : source.provider === "calendar"
            ? "google-personal"
            : `${source.provider}-melusine`,
      ),
    ),
  ),
  correlationKeys: [`situation:${id}`],
  entityNames: [],
  suggestedNextAction,
  version: 2,
  createdAt: iso(-24),
  updatedAt: iso(-1),
});

const sourceAccounts: SourceAccount[] = [
  {
    id: "google-personal",
    userId: "demo-owner",
    provider: "gmail",
    accountKind: "PERSONAL",
    label: "Personal Google",
    email: "andresmd01@gmail.com",
    capabilities: ["gmail.read", "calendar.full"],
    selectedChannelIds: [],
    status: "CURRENT",
    connectedAt: iso(-720),
    createdAt: iso(-720),
    updatedAt: iso(-0.1),
  },
  {
    id: "google-work",
    userId: "demo-owner",
    provider: "gmail",
    accountKind: "WORK",
    label: "Melusine Google",
    email: "andres@melusine.studio",
    capabilities: ["gmail.read", "calendar.metadata"],
    selectedChannelIds: [],
    status: "CURRENT",
    connectedAt: iso(-700),
    createdAt: iso(-700),
    updatedAt: iso(-0.1),
  },
  ...([
    ["slack-melusine", "slack", "Melusine Slack"],
    ["asana-melusine", "asana", "Melusine Asana"],
    ["notion-rennes", "notion", "Rennes Move / Notion"],
  ] as const).map(([id, provider, label]) => ({
    id,
    userId: "demo-owner",
    provider,
    accountKind: provider === "notion" ? ("SCOPED" as const) : ("WORK" as const),
    label,
    capabilities: provider === "notion" ? ["page.scoped"] : ["read"],
    selectedChannelIds: [],
    status: "CURRENT" as const,
    connectedAt: iso(-600),
    createdAt: iso(-600),
    updatedAt: iso(-0.1),
  })),
];

const sourceHealth: SourceHealth[] = sourceAccounts.map((account, index) => ({
  id: account.id,
  userId: "demo-owner",
  sourceAccountId: account.id,
  status: "CURRENT",
  lastEventAt: iso(-(index + 1) / 30),
  lastSyncAt: iso(-(index + 2) / 30),
  lastReconciledAt: iso(-0.12),
  nextRenewalAt: iso(24),
  message: "Current",
  updatedAt: iso(-0.1),
}));

const signals: Signal[] = [
  {
    id: "signal-certificate",
    userId: "demo-owner",
    kind: "STALE_REVIEW",
    title: "Klaviyo Developer Certificate",
    question: "Still active?",
    description: "Last meaningful change 64 days ago.",
    options: ["Yes", "No", "Not sure"],
    evidence: [],
    status: "PENDING",
    answer: null,
    createdAt: iso(-4),
    resolvedAt: null,
  },
  {
    id: "signal-approvals",
    userId: "demo-owner",
    kind: "FOLLOW_UP",
    title: "Trust Care client approvals",
    question: "When should I follow up?",
    description: "Waiting 9 days.",
    options: ["Tomorrow", "Next week", "Don’t monitor"],
    evidence: [],
    status: "PENDING",
    answer: null,
    createdAt: iso(-3),
    resolvedAt: null,
  },
  {
    id: "signal-health-domain",
    userId: "demo-owner",
    kind: "DOMAIN_PROPOSAL",
    title: "Create a Health domain?",
    question: "Should medical commitments become a separate domain?",
    description: "XO found recurring appointments and administrative commitments.",
    options: ["Create domain", "Keep under Personal", "Not now"],
    evidence: [],
    status: "PENDING",
    answer: null,
    createdAt: iso(-2),
    resolvedAt: null,
  },
  {
    id: "signal-old-tasks",
    userId: "demo-owner",
    kind: "STALE_REVIEW",
    title: "Old certification tasks",
    question: "Are these administrative residue?",
    description: "3 overdue tasks · no activity for 120+ days.",
    options: ["Still active", "Archive from monitoring", "Review individually"],
    evidence: [],
    status: "PENDING",
    answer: null,
    createdAt: iso(-1),
    resolvedAt: null,
  },
];

export const demoSnapshot = (timeZone: string): BootstrapResponse => ({
  preferences: {
    timeZone,
    notificationsEnabled: false,
    notificationMode: "SHADOW",
    shadowStartedAt: iso(-48),
    shadowEndsAt: iso(120),
    askRetentionDays: 30,
    previewRetentionDays: 90,
  },
  domains: [
    {
      id: "work",
      userId: "demo-owner",
      name: "Work",
      description: "Melusine delivery and client commitments.",
      color: "#344B57",
      icon: "briefcase",
      status: "ACTIVE",
      source: "INITIAL",
      createdAt: iso(-720),
      updatedAt: iso(-1),
    },
    {
      id: "france",
      userId: "demo-owner",
      name: "France",
      description: "Rennes move and arrival readiness.",
      color: "#C6842F",
      icon: "globe",
      status: "ACTIVE",
      source: "INITIAL",
      createdAt: iso(-720),
      updatedAt: iso(-1),
    },
    {
      id: "personal",
      userId: "demo-owner",
      name: "Personal",
      description: "Health and personal administration.",
      color: "#7A6A86",
      icon: "user",
      status: "ACTIVE",
      source: "INITIAL",
      createdAt: iso(-720),
      updatedAt: iso(-1),
    },
  ],
  attentionObjects: [
    attention({
      id: "rennes-tuition",
      domainId: "france",
      title: "Confirm Rennes tuition schedule",
      summary: "Conflicting payment instructions could affect access.",
      dueIn: 2,
      score: 92,
      disposition: "INTERRUPT",
      sources: [
        evidence(
          "rennes-gmail",
          "gmail",
          "Conflicting payment instructions",
          "https://mail.google.com/",
          "Signed agreement: instalments due 15 Oct and 15 Jan.",
        ),
        evidence(
          "rennes-notion",
          "notion",
          "Rennes Move · tuition & enrolment",
          "https://app.notion.com/p/38bb0a86a4b28170969fff728cd20b6d",
          "Automated email requested proof of full payment by 1 Sep.",
        ),
      ],
      suggestedNextAction:
        "Email Rennes finance to confirm the signed schedule in writing.",
    }),
    attention({
      id: "launch-scope",
      domainId: "work",
      title: "Reply on launch scope",
      summary: "Client X is waiting for your decision.",
      dueIn: 7,
      score: 78,
      sources: [
        evidence(
          "scope-slack",
          "slack",
          "Client decision request",
          "https://app.slack.com/",
          "Client X asked for a decision on the revised proposal.",
        ),
        evidence(
          "scope-asana",
          "asana",
          "Approve revised launch scope",
          "https://app.asana.com/",
          "The decision unblocks budget approval and kickoff.",
        ),
      ],
      suggestedNextAction: "Confirm the reduced launch scope today.",
    }),
    attention({
      id: "tgv-luggage",
      domainId: "france",
      title: "Resolve TGV luggage plan",
      summary: "Departure is in 3 days.",
      dueIn: 9,
      score: 71,
      sources: [
        evidence(
          "tgv-notion",
          "notion",
          "TGV luggage policy",
          "https://app.notion.com/",
          "Large luggage needs labelling and space is limited.",
        ),
      ],
      suggestedNextAction: "Choose the luggage configuration this afternoon.",
    }),
    attention({
      id: "delivery-date",
      domainId: "work",
      title: "Approve revised delivery date",
      summary: "The decision protects next week’s capacity.",
      dueIn: 11,
      score: 68,
      sources: [
        evidence(
          "delivery-asana",
          "asana",
          "Revised delivery date",
          "https://app.asana.com/",
          "Delivery moved to protect next week’s delivery capacity.",
        ),
      ],
      suggestedNextAction: "Approve or reject the revised date before end of day.",
    }),
  ],
  events: [],
  timeBlocks: [
    {
      id: "dermatology",
      userId: "demo-owner",
      title: "Dermatology",
      kind: "EVENT",
      startsAt: iso(2),
      endsAt: iso(3),
      sourceAccountId: "google-personal",
      sourceTimeZone: timeZone,
      suggested: false,
    },
    {
      id: "lunch",
      userId: "demo-owner",
      title: "Lunch with Claire",
      kind: "EVENT",
      startsAt: iso(3.5),
      endsAt: iso(4.5),
      sourceAccountId: "google-personal",
      sourceTimeZone: timeZone,
      suggested: false,
    },
    {
      id: "sync",
      userId: "demo-owner",
      title: "Product sync",
      kind: "EVENT",
      startsAt: iso(7),
      endsAt: iso(8),
      sourceAccountId: "google-work",
      sourceTimeZone: timeZone,
      suggested: false,
    },
    {
      id: "focus-one",
      userId: "demo-owner",
      title: "Deep work",
      kind: "FOCUS",
      startsAt: iso(0.5),
      endsAt: iso(2),
      sourceAccountId: null,
      sourceTimeZone: timeZone,
      suggested: true,
    },
    {
      id: "focus-two",
      userId: "demo-owner",
      title: "Strategy review",
      kind: "FOCUS",
      startsAt: iso(5.5),
      endsAt: iso(8),
      sourceAccountId: null,
      sourceTimeZone: timeZone,
      suggested: true,
    },
  ],
  sourceAccounts,
  sourceHealth,
  signals,
  auditEvents: [
    {
      id: "audit-health",
      userId: "demo-owner",
      type: "source.health_audited",
      metadata: { accounts: 5 },
      createdAt: iso(-0.2),
    },
  ],
  syncRuns: [
    {
      id: "sync-completed",
      userId: "demo-owner",
      sourceAccountId: "google-work",
      syncRunId: "demo-sync",
      status: "COMPLETED",
      reason: "RECONCILIATION",
      startedAt: iso(-0.3),
      completedAt: iso(-0.25),
      recordsSeen: 24,
      queued: 3,
      unchanged: 21,
      createdAt: iso(-0.25),
    },
  ],
});
